import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { PageHeader } from '@/components/admin/PageHeader';
import { useHostHealth, type HealthStatus } from '@/hooks/useHostHealth';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Bug, CircleCheck, AlertTriangle, XCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { sv } from 'date-fns/locale';

type TimeRange = '24h' | '7d' | '30d';

const RANGE_OPTIONS: { value: TimeRange; label: string }[] = [
  { value: '24h', label: '24 timmar' },
  { value: '7d', label: '7 dagar' },
  { value: '30d', label: '30 dagar' },
];

function StatusIcon({ status }: { status: HealthStatus }) {
  switch (status) {
    case 'red': return <XCircle className="h-4 w-4 text-destructive shrink-0" />;
    case 'yellow': return <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />;
    case 'green': return <CircleCheck className="h-4 w-4 text-emerald-500 shrink-0" />;
  }
}

export default function AdminHostHealthPage() {
  const [range, setRange] = useState<TimeRange>('24h');
  const { data: hosts, isLoading } = useHostHealth(range);

  const redCount = hosts?.filter(h => h.status === 'red').length ?? 0;
  const yellowCount = hosts?.filter(h => h.status === 'yellow').length ?? 0;

  return (
    <AdminLayout>
      <PageHeader
        title="Host Health"
        description="Övervaka värdar med problem senaste tiden"
      />

      {/* Range filter */}
      <div className="flex gap-2 mb-6">
        {RANGE_OPTIONS.map(opt => (
          <Button
            key={opt.value}
            variant={range === opt.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setRange(opt.value)}
          >
            {opt.label}
          </Button>
        ))}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="pt-4 pb-3 flex items-center gap-3">
            <XCircle className="h-5 w-5 text-destructive" />
            <div>
              <p className="text-2xl font-bold">{isLoading ? '–' : redCount}</p>
              <p className="text-xs text-muted-foreground">Med fel</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            <div>
              <p className="text-2xl font-bold">{isLoading ? '–' : yellowCount}</p>
              <p className="text-xs text-muted-foreground">Med varningar</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 flex items-center gap-3">
            <CircleCheck className="h-5 w-5 text-emerald-500" />
            <div>
              <p className="text-2xl font-bold">✓</p>
              <p className="text-xs text-muted-foreground">Övriga friska</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Host list */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      ) : !hosts?.length ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            <CircleCheck className="h-8 w-8 mx-auto mb-2 text-emerald-500" />
            Inga värdar med problem under vald period.
          </CardContent>
        </Card>
      ) : (
        <div className="border rounded-lg divide-y">
          {hosts.map(host => (
            <div
              key={host.producer_id}
              className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors"
            >
              <StatusIcon status={host.status} />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{host.producer_name}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {host.latest_event_key}
                  {' · '}
                  {formatDistanceToNow(new Date(host.latest_at), { addSuffix: true, locale: sv })}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {host.error_count > 0 && (
                  <Badge variant="destructive" className="text-xs">
                    {host.error_count} fel
                  </Badge>
                )}
                {host.warn_count > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {host.warn_count} varningar
                  </Badge>
                )}
                <Button variant="ghost" size="sm" asChild>
                  <Link to={`/app/admin/system/host-debug?q=${host.producer_id}`}>
                    <Bug className="h-4 w-4 mr-1" />
                    Debug
                  </Link>
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
